import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Stethoscope, MapPin, Phone, Calendar, Clock, CheckCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicDoctorProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [form, setForm] = useState({ patient_name: '', patient_phone: '', patient_age: '', patient_gender: 'M', reason: '' });

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    axios.get(`${API}/public/doctors/${id}`).then(r => {
      setDoc(r.data);
    }).catch(() => toast.error('Doctor not found'));
    axios.get(`${API}/public/doctors/${id}/slots?from_date=${today}`).then(r => setSlots(r.data.slots || [])).catch(() => {});
  }, [id]);

  const handleBook = async () => {
    if (!user) { toast.error('Please login to book'); window.location.href = '/login'; return; }
    if (!selectedSlot) { toast.error('Pick a slot'); return; }
    if (!form.patient_name || !form.patient_phone) { toast.error('Name & phone required'); return; }
    setBooking(true);
    try {
      await axios.post(`${API}/practice/appointments/book`, {
        doctor_id: id,
        clinic_id: selectedSlot.clinic_id,
        slot_id: selectedSlot.id,
        ...form,
        patient_age: form.patient_age ? parseInt(form.patient_age) : null,
      }, { withCredentials: true });
      toast.success('Appointment booked! Doctor will see your booking shortly.');
      setSelectedSlot(null);
      // Refresh slots
      const today = new Date().toISOString().split('T')[0];
      const r = await axios.get(`${API}/public/doctors/${id}/slots?from_date=${today}`);
      setSlots(r.data.slots || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed');
    } finally { setBooking(false); }
  };

  if (!doc) return <div className="min-h-screen bg-surface-secondary"><Navbar /><div className="pt-28 text-center text-secondary">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="public-doctor-profile">
      <Navbar />
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <section className="card mb-6">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Stethoscope size={48} weight="duotone" className="text-primary" />
              </div>
              <div className="flex-1 min-w-[240px]">
                <h1 className="font-spectral text-3xl font-bold text-primary">{/^dr\.?\s/i.test(doc.name || '') ? doc.name : `Dr. ${doc.name}`}</h1>
                <p className="text-accent font-medium">{doc.specialty}{doc.subspecialty && ` · ${doc.subspecialty}`}</p>
                {doc.qualifications && <p className="text-sm text-secondary mt-1">{doc.qualifications}</p>}
                {doc.years_experience && <p className="text-sm text-secondary">{doc.years_experience}+ years experience</p>}
                {doc.languages && <p className="text-xs text-secondary mt-1">Speaks: {doc.languages}</p>}
                {doc.registration_number && (
                  <p className="text-xs text-secondary mt-1" data-testid="doctor-registration">
                    Registration: {doc.medical_council || 'Medical Council'} No. {doc.registration_number}
                  </p>
                )}
                <span className="inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full text-xs bg-green-50 text-green-700">
                  <CheckCircle size={12} weight="fill" /> Verified specialist
                </span>
              </div>
            </div>
            {doc.bio && <p className="mt-4 text-secondary leading-relaxed">{doc.bio}</p>}
          </section>

          {/* Clinics */}
          {doc.clinics?.length > 0 && (
            <section className="card mb-6">
              <h2 className="font-semibold text-primary mb-3">Practices at</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {doc.clinics.map((c, idx) => (
                  <div key={c.id} className="p-4 border border-primary/10 rounded-lg" data-testid={`clinic-${idx}`}>
                    <h3 className="font-medium text-primary">{c.name}</h3>
                    <p className="text-xs text-secondary mt-1 inline-flex items-center gap-1"><MapPin size={12} /> {c.address}</p>
                    {c.phone && <p className="text-xs text-secondary mt-1 inline-flex items-center gap-1"><Phone size={12} /> {c.phone}</p>}
                    {c.timings && <p className="text-xs text-secondary mt-1 inline-flex items-center gap-1"><Clock size={12} /> {c.timings}</p>}
                    <p className="text-sm font-semibold text-primary mt-2">₹{c.consultation_fee} consultation</p>
                    {c.google_maps_url && <a href={c.google_maps_url} target="_blank" rel="noopener noreferrer" className="text-xs text-accent underline mt-1 inline-block">View on Maps</a>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Slots */}
          <section className="card mb-6">
            <h2 className="font-semibold text-primary mb-3 inline-flex items-center gap-2"><Calendar size={20} weight="duotone" /> Book an Appointment</h2>
            {slots.length === 0 ? (
              <p className="text-sm text-secondary">No open slots right now. Please check back later or message us on WhatsApp.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {slots.map((s, idx) => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedSlot(s)}
                    className={`p-3 rounded-lg border text-sm text-left ${
                      selectedSlot?.id === s.id ? 'border-primary bg-primary/5' : 'border-primary/20 hover:border-primary/60'
                    }`}
                    data-testid={`slot-${idx}`}
                  >
                    <p className="font-medium text-primary">{s.date}</p>
                    <p className="text-xs text-secondary">{s.start_time} - {s.end_time}</p>
                  </button>
                ))}
              </div>
            )}

            {selectedSlot && (
              <div className="mt-4 p-4 rounded-lg bg-surface-secondary space-y-2" data-testid="booking-form">
                <p className="text-sm font-medium text-primary">Selected: {selectedSlot.date} · {selectedSlot.start_time}-{selectedSlot.end_time}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input type="text" placeholder="Patient Name *" value={form.patient_name} onChange={e => setForm({ ...form, patient_name: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" data-testid="book-name-input" />
                  <input type="tel" placeholder="Phone *" value={form.patient_phone} onChange={e => setForm({ ...form, patient_phone: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" data-testid="book-phone-input" />
                  <input type="number" placeholder="Age" value={form.patient_age} onChange={e => setForm({ ...form, patient_age: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg" />
                  <select value={form.patient_gender} onChange={e => setForm({ ...form, patient_gender: e.target.value })} className="px-3 py-2 border border-primary/20 rounded-lg">
                    <option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
                  </select>
                </div>
                <textarea placeholder="Reason for visit (optional)" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} rows={2} className="w-full px-3 py-2 border border-primary/20 rounded-lg" />
                <button onClick={handleBook} disabled={booking} className="btn-primary w-full py-2 disabled:opacity-50" data-testid="confirm-book-button">
                  {booking ? 'Booking...' : 'Confirm Booking'}
                </button>
                <p className="text-xs text-secondary text-center">Payment will be collected at the clinic. Confirmation will be sent via WhatsApp.</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
